/* eslint-disable no-shadow */
import { ValidateJSModel, ValidateJSOptions } from 'forms/shared/types/validatejs';
import locationAddressModelValidator from 'forms/shared/validators/models/location/models/address';
import phoneModelValidator from 'forms/shared/validators/models/phone/phone';
import { hasYesOrNo } from 'models/validate';
import { DEFAULT_LATEST, TODAY } from 'constants/dateLimits';
import {
  getSafeDateTime, setToStartOfMonth, getApplicantRequiredDuration, getThresholdYears, sortSafeDateTimes, isSafeDateLater
} from 'helpers/date';
import { SafeDateTime } from 'helpers/types';
import { isPVQForm } from 'helpers/form';
import emailModelValidator from 'forms/shared/validators/models/pvqEmail';
import { disciplineOptions, militaryDisciplinaryProcedureNamesPvq } from 'constants/enums/militaryOptions';
import pvqLocationModelValidator from 'forms/shared/validators/models/location/pvqLocation';
import name from 'forms/shared/validators/models/name/name';
import { REQUIRED } from 'constants/errors';
import pvqNameModelValidator from 'forms/shared/validators/models/pvqName/name';
import {
  SupervisorModel,
  AdditionalActivityModel,
  ReprimandModel,
  ReasonLeftModel,
  ReasonModel,
} from '../types';

export const supervisorValidator: ValidateJSModel<SupervisorModel> = {
  SupervisorName: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }
    const { HasDifferentSupervisorAddress } = attributes;
    const { requireSupervisorAddress } = options;
    if (HasDifferentSupervisorAddress?.value === 'No' && !requireSupervisorAddress) { return {}; }

    return { hasValue: true };
  },

  Title: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }

    const { HasDifferentSupervisorAddress } = attributes;
    const { requireSupervisorAddress } = options;
    if (HasDifferentSupervisorAddress?.value === 'No' && !requireSupervisorAddress) { return {}; }

    return { hasValue: true };
  },

  EmailNotApplicable: {},

  Email: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }
    if (attributes.EmailNotApplicable?.applicable === false) { return {}; }

    if (options?.requireEmploymentHistorySupervisorEmail) {
      return { hasValue: { validator: { email: true } } };
    }
    return {};
  },

  Address: (value, attributes, attributeName, options) => {
    const { HasDifferentSupervisorAddress } = attributes;
    const { requireSupervisorAddress } = options;
    if (HasDifferentSupervisorAddress?.value === 'No' && !requireSupervisorAddress) { return {}; }

    return { presence: true, location: { validator: locationAddressModelValidator } };
  },

  Telephone: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }
    const { HasDifferentSupervisorAddress } = attributes;
    const { requireSupervisorAddress } = options;

    if (HasDifferentSupervisorAddress?.value === 'No' && !requireSupervisorAddress) { return {}; }

    return { presence: true, model: { validator: phoneModelValidator, requireNumber: true } };
  },

  HasDifferentSupervisorAddress: (value, attributes, attributeName, options = {}) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }
    if (options && options.requireHistoryEmployeeSupervisorPhysicalAddress) {
      return {
        presence: true,
        hasValue: { validator: hasYesOrNo },
      };
    }
    return {};
  },
};

// TODO: split validators into separate files
export const historyEmploymentAdditionalValidator: ValidateJSModel<AdditionalActivityModel> = {
  Has: {},

  Position: (value, attributes) => {
    if (attributes.Has?.value === 'Yes') { return { presence: true, hasValue: true }; }

    return {};
  },

  Supervisor: (value, attributes) => {
    if (attributes.Has?.value === 'Yes') { return { presence: true, hasValue: true }; }

    return {};
  },

  DatesEmployed: (value, attributes, attributeName, options) => {
    if (attributes.Has?.value !== 'Yes') { return {}; }
    const { applicantBirthdate, latest } = options;
    const thresholdYears = getThresholdYears(options?.formType);
    const applicantEighteenthBirthdate: SafeDateTime | undefined = options?.applicantBirthdate && setToStartOfMonth(getSafeDateTime(getSafeDateTime(options.applicantBirthdate)?.datetime?.plus({ years: 18 })));

    const { coverage } = getApplicantRequiredDuration({
      requiredDuration: options.requireYears ? { years: options.requireYears } : undefined,
      applicantBirthdate,
      thresholdYears,
      type: 'employment'
    });

    const toDate = getSafeDateTime(value?.to);
    const requiredDate = setToStartOfMonth(getSafeDateTime(TODAY.minus(coverage)));
    let earliestDate;
    let earliestMessage;

    if (options?.formType && options?.formType === 'SF86' && applicantEighteenthBirthdate?.valid && requiredDate?.valid && isSafeDateLater(requiredDate, applicantEighteenthBirthdate)) {
      earliestDate = applicantEighteenthBirthdate;
      earliestMessage = 'error.beforeDuration';
    } else if (requiredDate?.valid) {
      earliestDate = requiredDate;
      earliestMessage = 'error.beforeDuration';
    }

    const beyondRequired = (toDate?.valid && earliestDate?.valid)
      ? isSafeDateLater(earliestDate, toDate) : false;

    return {
      presence: true,
      daterange: {
        earliest: beyondRequired ? earliestDate.datetime : applicantBirthdate,
        latest,
        tooEarly: beyondRequired ? earliestMessage : 'error.applicantMin',
        tooLate: 'error.additionalFuture',
        requireYear: false,
      },
    };
  },
};

function getSortedEmploymentDates(dates: any, additionalDates: any): SafeDateTime[] {
  let employmentDates: SafeDateTime[] = [];

  if (dates?.from) {
    employmentDates.push(getSafeDateTime(dates.from));
  }

  if (additionalDates) {
    for (let i = 0; i < additionalDates.length; i++) {
      if (additionalDates[i]?.from) {
        employmentDates.push(getSafeDateTime(additionalDates[i]?.from));
      }
    }

    if (employmentDates?.length > 1) {
      employmentDates = sortSafeDateTimes(employmentDates);
    }
  }

  return employmentDates;
}

// TODO: split out validators into separate files
export const historyEmploymentReprimandValidator: ValidateJSModel<ReprimandModel> = {
  Has: {},

  Text: { presence: true, hasValue: true },

  Date: (value, attributes, attributeName, options) => {
    const requiredDate = setToStartOfMonth(getSafeDateTime(TODAY.minus({ years: options.recordYears })));
    const { Dates, additionalDates } = options;
    let earliestDate;
    let earliestMessage;
    const employmentDates = getSortedEmploymentDates(Dates, additionalDates);

    if (employmentDates?.length > 0 && employmentDates[0]?.valid && employmentDates[0]?.datetime > requiredDate.datetime) {
      earliestDate = employmentDates[0].datetime;
      earliestMessage = 'error.beforeHired';
    } else if (options.recordYears) {
      earliestDate = requiredDate.datetime;
      earliestMessage = 'error.beforeDuration';
    } else {
      earliestDate = options.applicantBirthdate;
      earliestMessage = 'error.applicantMin';
    }

    return {
      presence: true,
      date: {
        requireDay: false,
        requireYear: false,
        earliest: earliestDate,
        latest: DEFAULT_LATEST,
        tooEarly: earliestMessage,
      },
    };
  },

  DisciplinaryAction: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {
        hasValue: {
          validator: {
            inclusion: (options?.isMilitaryReprimand) ? militaryDisciplinaryProcedureNamesPvq : disciplineOptions,
          },
        },
      };
    }

    return {};
  },

  DisciplinaryActionOtherExplaination: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType) && attributes?.DisciplinaryAction?.value === 'Other') {
      return { presence: true, hasValue: true };
    }
    return {};
  },

  GivenBy: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType) && attributes.GivenByNotApplicable?.applicable === true) {
      return {
        presence: true,
        model: {
          validator: pvqNameModelValidator,
          requireMiddleName: false,
        }
      };
    }

    return {};
  },
};

/**
 * This will return true if the Reasons property should be validated.
 * @param allowsReasonLeft - Must include property Dates
 * @returns - boolean
 */
export const allowsReasonLeft = (reasonLeftOptions: ValidateJSOptions) => {
  const employmentEnd = getSafeDateTime(reasonLeftOptions?.Dates?.to);
  const earliestDate = setToStartOfMonth(getSafeDateTime(TODAY.minus({ years: reasonLeftOptions?.recordYears })));
  return !(reasonLeftOptions?.Dates?.present || (employmentEnd.valid && earliestDate.valid && (employmentEnd?.datetime < earliestDate?.datetime)));
};

export const reasonItemValidator = (reasonItemOptions: ValidateJSOptions): ValidateJSModel<ReasonModel> => {
  const validateReasons = allowsReasonLeft(reasonItemOptions);
  return {
    Has: () => {
      if (validateReasons) {
        return { presence: true, hasValue: true };
      }
      return {};
    },
    Reason: (value, attributes) => {
      if (validateReasons && attributes.Has?.value === 'Yes') {
        return { presence: true, hasValue: true };
      }
      return {};
    },
    Date: (value, attributes, attributeName, options) => {
      if (!validateReasons || attributes.Has?.value !== 'Yes') {
        return {};
      }

      const requiredDate = setToStartOfMonth(getSafeDateTime(TODAY.minus({ years: options.recordYears })));
      const { Dates, additionalDates } = options;
      let earliestDate;
      let earliestMessage;
      const employmentDates = getSortedEmploymentDates(Dates, additionalDates);

      if (employmentDates?.length > 0 && employmentDates[0]?.valid && employmentDates[0]?.datetime > requiredDate.datetime) {
        earliestDate = employmentDates[0].datetime;
        earliestMessage = 'error.beforeHired';
      } else if (options.recordYears) {
        earliestDate = requiredDate.datetime;
        earliestMessage = 'error.beforeDuration';
      } else {
        earliestDate = options.applicantBirthdate;
        earliestMessage = 'error.applicantMin';
      }

      return {
        presence: true,
        date: {
          requireYear: false,
          earliest: earliestDate,
          tooEarly: earliestMessage,
          latest: DEFAULT_LATEST,
        },
      };
    },
    Text: (value, attributes) => {
      if (validateReasons && attributes.Has?.value === 'Yes') {
        return { presence: true, hasValue: true };
      }
      return {};
    },
  };
};
export const reasonLeftValidator = (reasonLeftOptions: ValidateJSOptions): ValidateJSModel<ReasonLeftModel> => {
  const validateReasons = allowsReasonLeft(reasonLeftOptions);
  return {
    ReasonDescription: { presence: true, hasValue: true },
    Reasons: (value, attributes, attributeName, options) => {
      if (validateReasons) {
        return {
          presence: true,
          branchCollection: {
            validator: reasonItemValidator(options),
            Dates: options?.Dates,
            additionalDates: options?.additionalDates,
          }
        };
      }
      return {};
    },
  };
};

export const pvqAssignmentValidator = {
  AssignedUnitOrg: (value, attributes, attributeName, options) => ({ hasValue: true }),
  AssignedUnitOrgDateRange: (value, attributes, attributeName, options) => ({
    daterange: { earliest: setToStartOfMonth(options?.applicantBirthdate), latest: DEFAULT_LATEST },
  }),
  AssignedUnitOrgTelephone: (value, attributes, attributeName, options) => ({
    presence: true,
    model: {
      validator: phoneModelValidator,
      requireNumber: true,
      requireNumberType: true,
    },
  }),
  AssignedUnitOrgDutyStatus: (value, attributes, attributeName, options) => ({
    hasValue: true,
  }),
  AssignedUnitOrgTitle: (value, attributes, attributeName, options) => ({
    hasValue: true,
  })
};

export const pvqSupervisorValidator = {
  SupervisorNamePvq: (value, attributes, attributeName, options) => (
    {
      presence: true,
      model: {
        validator: pvqNameModelValidator,
        requireMiddleName: false
      }
    }
  ),
  Title: (value, attributes, attributeName, options) => {
    if (options.hasNoTitle) { return {}; }
    if (attributes?.TitleNotApplicable?.applicable === false) { return {}; }
    return {
      presence: true,
      hasValue: true,
    };
  },
  SupervisorAssignedUnitOrg: (value, attributes, attributeName, options) => {
    if (options.isMilitarySupervisor) {
      if (attributes?.SupervisorAssignedUnitOrgNotApplicable?.applicable === false) { return {}; }
      return {
        presence: true,
        hasValue: true,
      };
    }
    return {};
  },
  Telephone: (value, attributes, attributeName, options) => {
    if (!options.hasMultiTelephone) {
      return {
        presence: true,
        model: {
          validator: phoneModelValidator,
          requireNumberType: true,
        }
      };
    }
    return {};
  },
  Telephones: (value, attributes, attributeName, options) => {
    if (options.hasMultiTelephone) {
      return {
        presence: true,
        branchCollection: {
          validator: {
            Telephone: {
              model: {
                validator: phoneModelValidator,
                requireNumberType: true,
              },
            }
          },
        },
      };
    }
    return {};
  },
  Email: (value, attributes, attributeName, options) => {
    if (!options.hasMultiEmail) {
      if (attributes?.EmailNotApplicable?.applicable === false) { return {}; }
      return {
        presence: true,
        model: {
          validator: emailModelValidator,
        }
      };
    }
    return {};
  },
  Emails: (value, attributes, attributeName, options) => {
    if (options.hasMultiEmail) {
      return {
        presence: true,
        branchCollection: {
          validator: {
            Email: {
              model: {
                validator: emailModelValidator,
                requireEmailType: false,
              },
            },
          },
        },
      };
    }
    return {};
  },
  Address: (value, attributes, attributeName, options) => {
    const { AddressNotApplicable, InUsaNotApplicable } = attributes;
    if (AddressNotApplicable?.applicable === false || InUsaNotApplicable?.applicable === false) { return {}; }
    return {
      presence: true,
      location: {
        validator: pvqLocationModelValidator,
        requireStreet: true,
        requireIsMilitaryDiplomaticAddress: true,
        requireMilitaryDiplomaticInstallationName: true,
        requireMilitaryDiplomaticInstallationNameExplanation: options.requireMilitaryDiplomaticInstallationNameExplanation,
        requireApoFpoDpoPostcode: false,
      }
    };
  }

};

export const pvqHistoryEmploymentReprimandValidator: ValidateJSModel<ReprimandModel> = {
  DisciplinaryAction: (value, attributes, attributeName, options) => ({
    hasValue: {
      validator: {
        inclusion: (options?.isMilitaryReprimand) ? militaryDisciplinaryProcedureNamesPvq : disciplineOptions,
      },
    },
  }),

  DisciplinaryActionOtherExplaination: (value, attributes, attributeName, options) => {
    if (attributes?.DisciplinaryAction?.value === 'Other') {
      return { presence: true, hasValue: true };
    }
    return {};
  },

  Text: { presence: true, hasValue: true },

  Date: (value, attributes, attributeName, options) => {
    const requiredDate = setToStartOfMonth(getSafeDateTime(TODAY.minus({ years: options.recordYears })));
    const { Dates, additionalDates } = options;
    let earliestDate;
    let earliestMessage;
    const employmentDates = getSortedEmploymentDates(Dates, additionalDates);

    if (employmentDates?.length > 0 && employmentDates[0]?.valid && employmentDates[0]?.datetime > requiredDate.datetime) {
      earliestDate = employmentDates[0].datetime;
      earliestMessage = 'error.beforeHired';
    } else if (options.recordYears) {
      earliestDate = requiredDate.datetime;
      earliestMessage = 'error.beforeDuration';
    } else {
      earliestDate = options.applicantBirthdate;
      earliestMessage = 'error.applicantMin';
    }

    return {
      presence: true,
      date: {
        requireDay: false,
        requireYear: false,
        earliest: earliestDate,
        latest: DEFAULT_LATEST,
        tooEarly: earliestMessage,
      },
    };
  },

  GivenByRank: (value, attributes, attributeName, options) => {
    if (options.isMilitaryReprimand) {
      if (attributes?.RankUnknown?.applicable === false) { return {}; }
      return { presence: true, hasValue: true };
    }
    return {};
  },

  GivenBy: (value, attributes, attributeName, options) => {
    if (attributes?.GivenByNotApplicable?.applicable === false) {
      return {};
    }

    return {
      presence: true,
      model: { validator: pvqNameModelValidator, requireMiddleName: false }
    };
  },

  GivenByTelephone: (value, attributes, attributeName, options) => {
    if (options?.requireTelephone) {
      return {
        presence: true,
        branchCollection: {
          validator: {
            Telephone: {
              model: {
                validator: phoneModelValidator,
                requireNumberType: true,
              },
            },
          },
        },
      };
    }
    return {};
  },

  GivenByEmail: (value, attributes, attributeName, options) => {
    if (options?.requireEmail) {
      return {
        presence: true,
        branchCollection: {
          validator: {
            Email: {
              model: {
                validator: emailModelValidator,
                requireEmailType: false,
              },
            },
          },
        },
      };
    }
    return {};
  },

  Consequences: (value, attributes, attributeName, options) => {
    if (options.isMilitaryReprimand) {
      return {
        array: {
          presence: { allowEmpty: false },
          length: { minimum: 1, tooShort: REQUIRED },
        }
      };
    }
    return {};
  },

  ConsequencesOtherExplanation: (value, attributes, attributeName, options) => {
    if (attributes?.Consequences?.values?.includes('Other')) {
      return { presence: true, hasValue: true };
    }
    return {};
  },

  Has: {},
};

export const pvqDismissalItemValidator = {
  Date: (value, attributes, attributeName, options) => {
    if (attributes.DateNotApplicable) {
      return {};
    }

    if (options?.hasDate) {
      const requiredDate = setToStartOfMonth(getSafeDateTime(TODAY.minus({ years: options.recordYears })));
      const { Dates, additionalDates } = options;
      let earliestDate;
      let earliestMessage;
      const employmentDates = getSortedEmploymentDates(Dates, additionalDates);

      if (employmentDates?.length > 0 && employmentDates[0]?.valid && employmentDates[0]?.datetime > requiredDate.datetime) {
        earliestDate = employmentDates[0].datetime;
        earliestMessage = 'error.beforeHired';
      } else if (options.recordYears) {
        earliestDate = requiredDate.datetime;
        earliestMessage = 'error.beforeDuration';
      } else {
        earliestDate = options.applicantBirthdate;
        earliestMessage = 'error.applicantMin';
      }

      return {
        presence: true,
        date: {
          requireDay: false,
          requireYear: false,
          earliest: earliestDate,
          latest: DEFAULT_LATEST,
          tooEarly: earliestMessage,
        },
      };
    }
    return {};
  },
  Description: (value, attributes, attributeName, options) => ({ presence: true, hasValue: true }),
  InWriting: (value, attributes, attributeName, options) => ({
    presence: true,
    hasValue: {
      validator: hasYesOrNo
    }
  }),
  InPerson: (value, attributes, attributeName, options) => ({
    presence: true,
    hasValue: {
      validator: hasYesOrNo
    }
  }),
  GivenBy: (value, attributes, attributeName, options) => (
    {
      presence: true,
      model: {
        validator: pvqNameModelValidator,
        requireMiddleName: false
      }
    }),
  GivenByTelephone: (value, attributes, attributeName, options) => ({
    presence: true,
    branchCollection: {
      validator: {
        Telephone: {
          model: {
            validator: phoneModelValidator,
            requireNumberType: true,
          },
        },
      },
    },
  }),
  GivenByEmail: (value, attributes, attributeName, options) => ({
    presence: true,
    branchCollection: {
      validator: {
        Email: {
          model: {
            validator: emailModelValidator,
            requireEmailType: false,
          },
        },
      },
    },
  }),
};

export const pvqReasonLeftValidator = {
  ReasonDescription: { presence: true, hasValue: true },
};
